/**
 * Preparatory standards API client — Sprint 0143.
 *
 * One API surface for all 4 preparatory standards types. Backend lives
 * at /preparatory/{kind}-settings — see app/api/routers/preparatory_settings.py.
 */
import apiClient from '@/shared/api/client'

// ─── Kinds ──────────────────────────────────────────────────────────

export type PreparatoryKind = 'breaker-df' | 'finisher-df' | 'lap-former' | 'comber'

export const KIND_META: Record<PreparatoryKind, {
  label: string
  shortLabel: string
  deptCode: string
  machineType: string
}> = {
  'breaker-df': {
    label: 'Breaker Drawframe Settings',
    shortLabel: 'Breaker DF',
    deptCode: 'DRAW_FRAME_BREAKER',
    machineType: 'DRAW_FRAME_BREAKER',
  },
  'finisher-df': {
    label: 'Finisher Drawframe Settings',
    shortLabel: 'Finisher DF',
    deptCode: 'DRAW_FRAME_FINISHER',
    machineType: 'DRAW_FRAME_FINISHER',
  },
  'lap-former': {
    label: 'Lap Former Settings',
    shortLabel: 'Lap Former',
    deptCode: 'LAP_FORMER',
    machineType: 'LAP_FORMER',
  },
  comber: {
    label: 'Comber Settings',
    shortLabel: 'Comber',
    deptCode: 'COMBER',
    machineType: 'COMBER',
  },
}

// ─── Types ──────────────────────────────────────────────────────────

export interface PrepStandardBase {
  id: string
  plant_id: string
  machine_id: string | null
  count_id: string | null
  yarn_type: string | null
  sales_order_id: string | null
  num_deliveries: number
  delivery_speed_m_min: number
  output_hank: number
  std_efficiency_pct: number
  std_waste_pct: number
  std_can_kg: number
  std_can_length_m: number | null
  qc_measured_at: string | null
  qc_measured_by: string | null
  qc_sample_kg: number | null
  effective_from: string
  effective_to: string | null
  notes: string | null
  is_active: boolean
  created_at: string | null
  // Sprint 0144 follow-up — audit trail for the Updated column.
  updated_at?: string | null
  created_by?: string | null
  updated_by?: string | null
  // Lap Former extras (optional)
  std_lap_weight_kg?: number | null
  std_lap_length_m?: number | null
  lap_ktex?: number | null
  // Comber extras (optional)
  std_noil_pct?: number | null
  std_realization_pct?: number | null
  input_lap_ktex?: number | null
  reconciliation_tolerance_pct?: number | null
}

export type PrepStandardCreate = Partial<PrepStandardBase> & {
  plant_id: string
  num_deliveries: number
  delivery_speed_m_min: number
  output_hank: number
  effective_from: string
}

// ─── API surface ────────────────────────────────────────────────────

const BASE = (kind: PreparatoryKind) => `/preparatory/${kind}-settings`

export const preparatoryApi = {
  list: (kind: PreparatoryKind, params: {
    plant_id: string
    machine_id?: string
    count_id?: string
    active_only?: boolean
  }) => apiClient.get<PrepStandardBase[]>(BASE(kind), { params })
    .then((r) => r.data),

  get: (kind: PreparatoryKind, id: string) =>
    apiClient.get<PrepStandardBase>(`${BASE(kind)}/${id}`).then((r) => r.data),

  create: (kind: PreparatoryKind, body: PrepStandardCreate) =>
    apiClient.post<PrepStandardBase>(BASE(kind), body).then((r) => r.data),

  update: (kind: PreparatoryKind, id: string, body: Partial<PrepStandardBase>) =>
    apiClient.patch<PrepStandardBase>(`${BASE(kind)}/${id}`, body)
      .then((r) => r.data),

  deactivate: (kind: PreparatoryKind, id: string) =>
    apiClient.post<PrepStandardBase>(`${BASE(kind)}/${id}/deactivate`)
      .then((r) => r.data),

  // Sprint 0144 follow-up — Excel export, mirrors carding pattern.
  // Triggers a browser save via blob URL; never holds bytes in state.
  // Sprint 0146 QE — Quick Entry from prep-machine displays.
  // Same flow as cardingApi.quickEntry but kind-aware. Server resolves
  // the right standards model, runs the right formula, fires comber
  // reconciliation hook when relevant.
  quickEntry: async (kind: PreparatoryKind, body: {
    plant_id: string
    machine_id: string
    shift_id: string
    log_date: string
    count_id?: string | null
    production_kg?: number | null
    run_time_minutes?: number | null
    idle_time_minutes?: number | null
    power_fail_minutes?: number | null
    machine_efficiency_pct?: number | null
    // Comber-only
    input_lap_kg?: number | null
    noil_kg?: number | null
    waste_kg?: number | null
    // Lap Former — count-based
    laps_produced?: number | null
    avg_lap_weight_kg?: number | null
    faults: Array<{
      fault_type: 'DOUBLE_LAP' | 'OTHER_FAULTS' | 'SLIVER_BREAK'
                | 'CHUTE_FAULTS' | 'DOFFS'
      count: number
      root_cause?: string | null
      corrective_action?: string | null
    }>
    remarks?: string | null
  }) => (await apiClient.post<{
    log_id: string
    log_no: string
    kind: PreparatoryKind
    calc: {
      production_kg: number | null
      theoretical_kg: number | null
      expected_kg: number | null
      actual_efficiency_pct: number | null
      noil_pct?: number | null
      realization_pct?: number | null
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      comber_reconciliation?: any
      confidence: string
    }
    fault_entries: Array<{
      fault_type: string; count: number
      root_cause: string | null; corrective_action: string | null
    }>
    alerts_raised: number
    standard_applied: string | null
  }>(`/preparatory/quick-entry/${kind}`, body)).data,

  exportSettings: async (kind: PreparatoryKind, params: {
    plant_id: string
    machine_id?: string
    count_id?: string
    active_only?: boolean
  }) => {
    const r = await apiClient.get<Blob>(
      `${BASE(kind)}/export.xlsx`,
      { params, responseType: 'blob' },
    )
    const blob = new Blob([r.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${kind}_settings.xlsx`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 100)
  },

  // GET /preparatory/route?yarn_type=COMBED
  route: (params: { yarn_type?: string; plant_id?: string; count_id?: string }) =>
    apiClient.get<{
      route_type: 'COMBED' | 'CARDED'
      yarn_type: string
      sequence: string[]
      source: string
    }>('/preparatory/route', { params }).then((r) => r.data),
}
